/**
 * Configuration Persistence Module
 *
 * Handles saving and loading configurations to/from browser localStorage
 * and JSON files. Includes versioning, migration support, and metadata management.
 */

import type { ClusterConfiguration } from '../types/configuration';

/**
 * Current schema version
 * Increment this when making breaking changes to Configuration structure
 */
export const CURRENT_SCHEMA_VERSION = 1;
export const CURRENT_VERSION = 'v1.0';

/**
 * Storage keys for localStorage
 */
export const STORAGE_KEYS = {
  CURRENT_CONFIG: 'gpu_bom_calculator_config',
  CONFIG_LIST: 'gpu_bom_calculator_configs',
  SCHEMA_VERSION: 'gpu_bom_calculator_schema_version',
  LAST_SAVED: 'gpu_bom_calculator_last_saved',
} as const;

/**
 * Saved Configuration
 * Wraps Configuration with additional metadata
 */
export interface SavedConfiguration {
  id: string;
  name: string;
  description?: string;
  tags?: string[];
  version: string;
  schemaVersion: number;
  created: string;
  modified: string;
  config: ClusterConfiguration;
  configurationHash?: string;
}

/**
 * Storage Error
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public code: 'QUOTA_EXCEEDED' | 'PARSE_ERROR' | 'VERSION_MISMATCH' | 'NOT_FOUND' | 'UNKNOWN'
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Get current schema version from storage
 */
export function getStoredSchemaVersion(): number {
  try {
    const version = localStorage.getItem(STORAGE_KEYS.SCHEMA_VERSION);
    return version ? parseInt(version, 10) : 1;
  } catch (e) {
    return 1;
  }
}

/**
 * Set schema version in storage
 */
export function setStoredSchemaVersion(version: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SCHEMA_VERSION, String(version));
  } catch (e) {
    throw new StorageError('Failed to store schema version', 'UNKNOWN');
  }
}

/**
 * Generate unique ID for configuration
 */
function generateId(): string {
  return `config_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate hash for configuration (for change detection)
 * Simple implementation - could be enhanced with crypto.subtle.digest
 */
function generateConfigHash(config: ClusterConfiguration): string {
  const configString = JSON.stringify({
    scaling: config.scaling,
    gpu: config.gpu,
    server: config.server,
    network: config.network,
    rack: config.rack,
    power: config.power,
  });

  // Simple hash function (could use crypto.subtle.digest for production)
  let hash = 0;
  for (let i = 0; i < configString.length; i++) {
    const char = configString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `hash_${Math.abs(hash).toString(16)}`;
}

/**
 * Save configuration to localStorage
 *
 * @param config - Configuration to save
 * @param name - Optional name (uses config.name if not provided)
 * @param description - Optional description
 * @param tags - Optional tags for categorization
 * @returns Saved configuration with metadata
 */
export function saveToLocalStorage(
  config: ClusterConfiguration,
  name?: string,
  description?: string,
  tags?: string[]
): SavedConfiguration {
  if (!isLocalStorageAvailable()) {
    throw new StorageError('localStorage is not available', 'UNKNOWN');
  }

  try {
    const now = new Date().toISOString();
    const configName = name || config.name || 'Untitled Configuration';

    const savedConfig: SavedConfiguration = {
      id: config.id || generateId(),
      name: configName,
      description,
      tags,
      version: CURRENT_VERSION,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      created: now,
      modified: now,
      config: {
        ...config,
        name: configName,
        modifiedAt: new Date(now),
      },
      configurationHash: generateConfigHash(config),
    };

    // Save current config
    localStorage.setItem(STORAGE_KEYS.CURRENT_CONFIG, JSON.stringify(savedConfig));
    localStorage.setItem(STORAGE_KEYS.LAST_SAVED, now);
    setStoredSchemaVersion(CURRENT_SCHEMA_VERSION);

    // Add to config list
    const configList = loadConfigList();
    const existingIndex = configList.findIndex((c) => c.id === savedConfig.id);
    if (existingIndex >= 0) {
      configList[existingIndex] = savedConfig;
    } else {
      configList.push(savedConfig);
    }
    localStorage.setItem(STORAGE_KEYS.CONFIG_LIST, JSON.stringify(configList));

    return savedConfig;
  } catch (e) {
    if (e instanceof Error && e.name === 'QuotaExceededError') {
      throw new StorageError('Storage quota exceeded', 'QUOTA_EXCEEDED');
    }
    throw new StorageError(`Failed to save configuration: ${e}`, 'UNKNOWN');
  }
}

/**
 * Load configuration from localStorage
 *
 * @returns Configuration or null if not found
 */
export function loadFromLocalStorage(): SavedConfiguration | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const data = localStorage.getItem(STORAGE_KEYS.CURRENT_CONFIG);
    if (!data) {
      return null;
    }

    const savedConfig: SavedConfiguration = JSON.parse(data);

    // Check if migration is needed
    if (savedConfig.schemaVersion < CURRENT_SCHEMA_VERSION) {
      const migrated = migrateConfiguration(savedConfig);
      saveToLocalStorage(migrated.config, migrated.name);
      return migrated;
    }

    return savedConfig;
  } catch (e) {
    throw new StorageError('Failed to parse stored configuration', 'PARSE_ERROR');
  }
}

/**
 * Load list of all saved configurations
 *
 * @returns Array of saved configurations
 */
export function loadConfigList(): SavedConfiguration[] {
  if (!isLocalStorageAvailable()) {
    return [];
  }

  try {
    const data = localStorage.getItem(STORAGE_KEYS.CONFIG_LIST);
    if (!data) {
      return [];
    }

    return JSON.parse(data);
  } catch (e) {
    return [];
  }
}

/**
 * Load specific configuration by ID
 *
 * @param id - Configuration ID
 * @returns Configuration or null if not found
 */
export function loadConfigById(id: string): SavedConfiguration | null {
  const configList = loadConfigList();
  const config = configList.find((c) => c.id === id);

  if (!config) {
    return null;
  }

  // Check if migration is needed
  if (config.schemaVersion < CURRENT_SCHEMA_VERSION) {
    return migrateConfiguration(config);
  }

  return config;
}

/**
 * Delete configuration from localStorage
 *
 * @param id - Configuration ID to delete
 */
export function deleteConfig(id: string): void {
  if (!isLocalStorageAvailable()) {
    throw new StorageError('localStorage is not available', 'UNKNOWN');
  }

  try {
    const configList = loadConfigList();
    const filtered = configList.filter((c) => c.id !== id);
    localStorage.setItem(STORAGE_KEYS.CONFIG_LIST, JSON.stringify(filtered));

    // If we deleted the current config, clear it
    const current = loadFromLocalStorage();
    if (current?.id === id) {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_CONFIG);
    }
  } catch (e) {
    throw new StorageError(`Failed to delete configuration: ${e}`, 'UNKNOWN');
  }
}

/**
 * Clear all stored configurations
 */
export function clearAllConfigs(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_CONFIG);
    localStorage.removeItem(STORAGE_KEYS.CONFIG_LIST);
    localStorage.removeItem(STORAGE_KEYS.LAST_SAVED);
  } catch (e) {
    throw new StorageError(`Failed to clear configurations: ${e}`, 'UNKNOWN');
  }
}

/**
 * Migrate configuration from old schema to new schema
 *
 * @param savedConfig - Configuration to migrate
 * @returns Migrated configuration
 */
export function migrateConfiguration(savedConfig: SavedConfiguration): SavedConfiguration {
  const fromVersion = savedConfig.schemaVersion;
  let config = { ...savedConfig.config };

  // Migration chain - add new migrations here as schema evolves
  if (fromVersion < 1) {
    // Future migration: v0 -> v1
    // Example: config.newField = defaultValue;
  }

  // Update metadata
  return {
    ...savedConfig,
    version: CURRENT_VERSION,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    modified: new Date().toISOString(),
    config: {
      ...config,
      version: CURRENT_VERSION,
    },
  };
}

/**
 * Export configuration to JSON string
 *
 * @param config - Configuration to export
 * @param pretty - Whether to pretty-print JSON
 * @param description - Optional description
 * @param tags - Optional tags
 * @returns JSON string
 */
export function exportToJSON(
  config: ClusterConfiguration,
  pretty = true,
  description?: string,
  tags?: string[]
): string {
  const now = new Date().toISOString();
  const exportData: SavedConfiguration = {
    id: config.id || generateId(),
    name: config.name || 'Exported Configuration',
    description,
    tags,
    version: CURRENT_VERSION,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    created: now,
    modified: now,
    config: {
      ...config,
      modifiedAt: new Date(now),
    },
    configurationHash: generateConfigHash(config),
  };

  return JSON.stringify(exportData, null, pretty ? 2 : 0);
}

/**
 * Import configuration from JSON string
 *
 * @param jsonData - JSON string
 * @returns Imported configuration
 */
export function importFromJSON(jsonData: string): SavedConfiguration {
  try {
    const savedConfig: SavedConfiguration = JSON.parse(jsonData);

    // Validate structure
    if (!savedConfig.config) {
      throw new Error('Invalid configuration format: missing config field');
    }

    // Check if migration is needed
    if (savedConfig.schemaVersion < CURRENT_SCHEMA_VERSION) {
      return migrateConfiguration(savedConfig);
    }

    return savedConfig;
  } catch (e) {
    if (e instanceof SyntaxError) {
      throw new StorageError('Invalid JSON format', 'PARSE_ERROR');
    }
    throw new StorageError(`Failed to import configuration: ${e}`, 'PARSE_ERROR');
  }
}

/**
 * Download configuration as JSON file
 *
 * @param config - Configuration to download
 * @param filename - Output filename
 * @param description - Optional description
 * @param tags - Optional tags
 */
export function downloadJSON(
  config: ClusterConfiguration,
  filename = 'gpu-cluster-config.json',
  description?: string,
  tags?: string[]
): void {
  const json = exportToJSON(config, true, description, tags);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Import configuration from File object
 *
 * @param file - File object from input element
 * @returns Promise with imported configuration
 */
export async function importFromFile(file: File): Promise<SavedConfiguration> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const config = importFromJSON(text);
        resolve(config);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new StorageError(`Failed to read file: ${reader.error?.message}`, 'UNKNOWN'));
    };

    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Get storage usage information
 *
 * @returns Storage usage stats
 */
export function getStorageInfo(): {
  available: boolean;
  used: number;
  configCount: number;
  lastSaved: string | null;
} {
  if (!isLocalStorageAvailable()) {
    return {
      available: false,
      used: 0,
      configCount: 0,
      lastSaved: null,
    };
  }

  try {
    // Calculate used storage (rough estimate)
    let used = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }

    const configCount = loadConfigList().length;
    const lastSaved = localStorage.getItem(STORAGE_KEYS.LAST_SAVED);

    return {
      available: true,
      used,
      configCount,
      lastSaved,
    };
  } catch (e) {
    return {
      available: true,
      used: 0,
      configCount: 0,
      lastSaved: null,
    };
  }
}

/**
 * Compare two configurations
 *
 * @param config1 - First configuration
 * @param config2 - Second configuration
 * @returns Comparison result
 */
export function compareConfigurations(
  config1: ClusterConfiguration,
  config2: ClusterConfiguration
): {
  identical: boolean;
  differences: string[];
  summary: {
    gpuCountDelta: number;
    nodeCountDelta: number;
    rackCountDelta: number;
  };
} {
  const differences: string[] = [];

  // Compare GPU counts
  if (config1.scaling.totalGPUs !== config2.scaling.totalGPUs) {
    differences.push(
      `GPU count: ${config1.scaling.totalGPUs} → ${config2.scaling.totalGPUs} (${config2.scaling.totalGPUs > config1.scaling.totalGPUs ? '+' : ''}${config2.scaling.totalGPUs - config1.scaling.totalGPUs})`
    );
  }

  // Compare GPU platform
  if (config1.gpu.platformId !== config2.gpu.platformId) {
    differences.push(`GPU platform: ${config1.gpu.platformId} → ${config2.gpu.platformId}`);
  }

  // Compare servers
  if (config1.server.profileId !== config2.server.profileId) {
    differences.push(`Server: ${config1.server.profileId} → ${config2.server.profileId}`);
  }

  // Compare network
  if (JSON.stringify(config1.network) !== JSON.stringify(config2.network)) {
    differences.push('Network configuration changed');
  }

  // Compare power settings
  if (config1.power.coolingOverhead !== config2.power.coolingOverhead) {
    differences.push(
      `PUE: ${config1.power.coolingOverhead} → ${config2.power.coolingOverhead}`
    );
  }

  return {
    identical: differences.length === 0,
    differences,
    summary: {
      gpuCountDelta: config2.scaling.totalGPUs - config1.scaling.totalGPUs,
      nodeCountDelta: config2.scaling.totalNodes - config1.scaling.totalNodes,
      rackCountDelta: config2.scaling.totalComputeRacks - config1.scaling.totalComputeRacks,
    },
  };
}
